import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { buildClips } from './narration';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/demo-showcase/voiceovers`;
const Ctx = createContext(null);
export const useVoiceovers = () => useContext(Ctx);

export const VoiceoverProvider = ({ children }) => {
  const clips = useMemo(() => buildClips(), []);
  const byRef = useMemo(() => Object.fromEntries(clips.map((c) => [c.ref, c])), [clips]);
  const [items, setItems] = useState({});
  const [busy, setBusy] = useState({});
  const urls = useRef({});
  const current = useRef(null);
  const claim = useCallback((stop) => {
    if (current.current && current.current !== stop) current.current();
    current.current = stop;
  }, []);

  const load = useCallback(() => axios.get(API).then((r) => {
    setItems(Object.fromEntries(r.data.items.map((i) => [i.ref, i])));
  }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const generate = useCallback(async (ref) => {
    const c = byRef[ref];
    setBusy((b) => ({ ...b, [ref]: true }));
    try {
      const r = await axios.post(API, { ref, file_name: `${c.name}.mp3`, text: c.text });
      if (items[ref]?.text_hash !== r.data.text_hash) delete urls.current[ref];
      setItems((m) => ({ ...m, [ref]: r.data }));
      return r.data;
    } finally {
      setBusy((b) => ({ ...b, [ref]: false }));
    }
  }, [byRef, items]);

  const audioBlob = useCallback(async (ref) => {
    const r = await axios.get(`${API}/${ref}/audio`, { responseType: 'blob' });
    return r.data;
  }, []);

  const audioUrl = useCallback(async (ref) => {
    if (!urls.current[ref]) urls.current[ref] = URL.createObjectURL(await audioBlob(ref));
    return urls.current[ref];
  }, [audioBlob]);

  const value = useMemo(() => ({ clips, byRef, items, busy, load, generate, audioUrl, audioBlob, claim }),
    [clips, byRef, items, busy, load, generate, audioUrl, audioBlob, claim]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
